/** @jsxImportSource @emotion/react */
import React, { memo, useContext } from 'react'
import { GENERIC_PURECARS_DOMAIN } from '@signal/utils/constants'
import { setCookie } from '@signal/utils/cookies'
/* if-not-on-site-start */
import { AuthError } from '@signal/utils/errors'
import { PC_AUTH_COOKIE, PC_AUTH_COOKIE_QA } from '../auth/constants'
import { getAuthFailureRedirectUrl } from '../auth/utils/redirects'
/* if-not-on-site-end */
import SWRRootContext from '../swr/RootContext'
import { useSWRWithCache } from '../swr/hooks'
import { ContextFetchHookType, ContextFetchLoaderPropsType } from './types'
import { createFetchContext, createFetchContextProps } from './FetchContext'

/**
 * @component ContextFetcher
 * @template <FetchDataType> - the data type handled from this context fetcher
 * @template <FetchHookParamsType> - the parameters that need to be sent to the context fetch hook function
 * The <ContextFetcher> React component which handles a single context fetch
 */
export function ContextFetcher<FetchDataType, FetchHookParamsType>({
  children,
  fetchHook = useSWRWithCache as ContextFetchHookType<
    FetchDataType,
    FetchHookParamsType
  >,
  FetchContext = createFetchContext<FetchDataType>(),
  // any remaining properties will be sent to the hook function
  ...hookParams
}: ContextFetchLoaderPropsType<
  FetchDataType,
  FetchHookParamsType
>): JSX.Element {
  const { rethrowContextFetchErrors } = useContext(SWRRootContext)
  const fetchContext = createFetchContextProps<FetchDataType>({
    isFetching: true
  })

  try {
    const result = fetchHook((hookParams as unknown) as FetchHookParamsType)
    if (result) {
      fetchContext.isFetching = false
      fetchContext.sawError = false
      fetchContext.response = result
    }
  } catch (error) {
    fetchContext.sawError = true

    /* if-not-on-site-start */
    // TODO: we need to figure out which cookie to delete in which environment
    // (maybe should be configured from root eventually)
    if (error instanceof AuthError) {
      // if we see an auth error, our cookies must not be valid (prod/QA shift).  Clear cookies and reload
      setCookie({
        name: PC_AUTH_COOKIE,
        domain: GENERIC_PURECARS_DOMAIN,
        value: ''
      })
      setCookie({
        name: PC_AUTH_COOKIE_QA,
        domain: GENERIC_PURECARS_DOMAIN,
        value: ''
      })

      const authRedirect = getAuthFailureRedirectUrl()
      window.location.href = authRedirect
      return <></>
    }
    /* if-not-on-site-end */

    if (rethrowContextFetchErrors) {
      throw error
    }
  }

  return (
    <FetchContext.Provider value={fetchContext}>
      {children}
    </FetchContext.Provider>
  )
}

// note - do not use this in typescript, use castFetchContext instead
export default memo(ContextFetcher)
